import React, { FunctionComponent, useContext } from 'react';
import { keyBy } from 'lodash';
import { Intl } from '@erecruit/core';
import { PositionTemplateFieldType } from '@erecruit/types';
import {
  H1,
  InputNumber,
  FormItem,
  Input,
  FormItemColumns,
  UsersIcon,
  TextArea,
  FormContextType,
  FormContext,
} from '@erecruit/ui-kit';

import { Disclaimer } from '../Disclaimer';
import DynamicFormItems from '../DynamicFormItems/DynamicFormItems';
import { generateFieldOptions } from '../../helpers';
import { PositionField } from '../../types';
import { SelectField } from '../DynamicFormItems/Fields';

const { FormattedMessage, injectIntl } = Intl;

export interface GeneralStepProps {
  fields: PositionField[];
}

export interface GeneralStepForm {
  positionTitle: string;
  positionType: number;
  numberOfPositions: string;
  positionDescription: string;
}

const GeneralStep: FunctionComponent<GeneralStepProps &
  Intl.InjectedIntlProps> = ({ intl, fields }) => {
  const form = useContext<FormContextType<GeneralStepForm>>(FormContext);
  const keyFields = keyBy(fields, 'fieldType');

  const { getFieldsValue } = form;
  const {
    positionTitle,
    numberOfPositions,
    positionDescription,
  } = getFieldsValue();

  const positionTitleField = keyFields[PositionTemplateFieldType.PositionTitle];
  const positionTypeField = keyFields[PositionTemplateFieldType.PositionType];
  const numberOfPositionsField =
    keyFields[PositionTemplateFieldType.NumberOfPositions];
  const positionDescriptionField =
    keyFields[PositionTemplateFieldType.PositionDescription];

  return (
    <div>
      <H1>
        <FormattedMessage id={messageIds.vms.newPositionWizard.general.title} />
      </H1>
      <Disclaimer>
        <FormattedMessage
          id={messageIds.vms.newPositionWizard.general.disclaimer}
        />
      </Disclaimer>
      {positionTitleField && (
        <FormItem
          id="positionTitle"
          label={intl.formatMessage({
            id: messageIds.vms.newPositionWizard.general.positionTitle,
          })}
          hasFeedback
          options={generateFieldOptions(positionTitleField, positionTitle)}
        >
          <Input
            placeholder={intl.formatMessage({
              id:
                messageIds.vms.newPositionWizard.general
                  .positionTitlePlaceholder,
            })}
            disabled={
              positionTitleField.settings && positionTitleField.settings.locked
            }
          />
        </FormItem>
      )}
      <FormItemColumns>
        {positionTypeField && (
          <SelectField
            field={positionTypeField}
            messageFormatId="vms.newPositionWizard"
          />
        )}
        {numberOfPositionsField && (
          <FormItem
            id="numberOfPositions"
            label={intl.formatMessage({
              id: messageIds.vms.newPositionWizard.general.numberOfPositions,
            })}
            hasFeedback
            options={generateFieldOptions(
              numberOfPositionsField,
              numberOfPositions,
            )}
          >
            <InputNumber
              icon={UsersIcon}
              min={1}
              style={{ width: '100%' }}
              disabled={
                numberOfPositionsField.settings &&
                numberOfPositionsField.settings.locked
              }
            />
          </FormItem>
        )}
      </FormItemColumns>
      {positionDescriptionField && (
        <FormItem
          id="positionDescription"
          label={intl.formatMessage({
            id: messageIds.vms.newPositionWizard.general.positionDescription,
          })}
          options={generateFieldOptions(
            positionDescriptionField,
            positionDescription,
          )}
        >
          <TextArea
            autoSize={{ minRows: 4 }}
            disabled={
              positionDescriptionField.settings &&
              positionDescriptionField.settings.locked
            }
          />
        </FormItem>
      )}
      <DynamicFormItems section="general" fields={fields} />
    </div>
  );
};

export default injectIntl(GeneralStep);
